<template>
  <MarketCard>
    <div :class="classes.content">
      <LinkWrapper
        v-if="showBadges && badgesEnabled"
        :class="classes.badgeLink"
        :href="event.eventUrl"
        target="_blank"
        @click="() => handleClick()"
      >
        <TeamBadge
          :class="['badge', classes.badge, classes.badgeLeft]"
          :src="competitors[0].badge"
          :countryCode="competitors[0].colors.country_code_a2"
          :defaultBadge="'home'"
          :borderColor="useTeamColors ? colors.home?.banner.background : stroke"
          :borderWidth="'2px'"
        />
      </LinkWrapper>
      <LinkWrapper
        v-if="showBadges && jerseysEnabled"
        :class="classes.badgeLink"
        :href="event.eventUrl"
        target="_blank"
        @click="() => handleClick()"
      >
        <TeamJersey
          :class="['badge', classes.badge, classes.jersey, classes.badgeLeft]"
          :colors="competitors[0].colors"
          :borderWidth="'2px'"
          homeOrAwayTeam="home"
          transform="scale(-1, 1)"
        />
      </LinkWrapper>
      <div :class="classes.wrapper">
        <div :class="classes.header">
          <CardHeaderLeaderboard
            :eventId="eventId"
            style="border-bottom: none; padding: unset"
          />
          <MarketName :marketId="marketId" :class="classes.marketName" />
        </div>
        <div :class="classes.body">
          <div :class="[classes.odds, 'left']">
            <WinOption
              :stagger="0"
              :eventId="eventId"
              :class="[classes.option, layoutClass]"
              :optionId="ftrMarkets[0].options[0]"
              :alignment="'left'"
              :competitor="competitors[0]"
              :showBadges="showBadges"
              homeOrAwayTeam="home"
            />
          </div>
          <div :class="[classes.odds, 'center']">
            <TeamVersusIcon :eventId="eventId" v-bind="$attrs" />
          </div>
          <div :class="[classes.odds, 'right']">
            <WinOption
              :stagger="1"
              :eventId="eventId"
              :class="[classes.option, layoutClass]"
              :optionId="ftrMarkets[0].options[1]"
              :alignment="'right'"
              :competitor="competitors[1]"
              :showBadges="showBadges"
              homeOrAwayTeam="away"
            />
          </div>
        </div>
      </div>
      <LinkWrapper
        v-if="showBadges && badgesEnabled"
        :class="classes.badgeLink"
        :href="event.eventUrl"
        target="_blank"
        @click="() => handleClick()"
      >
        <TeamBadge
          :class="['badge', classes.badge, classes.badgeRight]"
          :src="competitors[1].badge"
          :countryCode="competitors[1].colors.country_code_a2"
          :defaultBadge="'home'"
          :borderColor="useTeamColors ? colors.away?.banner.background : stroke"
          :borderWidth="'2px'"
        />
      </LinkWrapper>
      <LinkWrapper
        v-if="showBadges && jerseysEnabled"
        :class="classes.badgeLink"
        :href="event.eventUrl"
        target="_blank"
        @click="() => handleClick()"
      >
        <TeamJersey
          :class="['badge', classes.badge, classes.jersey, classes.badgeRight]"
          :borderWidth="'2px'"
          homeOrAwayTeam="away"
        />
      </LinkWrapper>
    </div>
  </MarketCard>
</template>

<script lang="ts">
import { ComputedRef, defineComponent, ref, computed } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  composeEvent,
  composeBetOption,
  composeEventCompetitors,
  composeHomeAwayOptions
} from "@fresh8/ad-data-composition/src/betData";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import MarketName from "../../../MarketName.vue";
import CardHeaderLeaderboard from "../../../CardHeaderLeaderboard.vue";
import MarketCard from "../../../styled/MarketCard.vue";
import TeamBadge from "../../../TeamBadge.vue";
import TeamJersey from "../../../team-jerseys/TeamJerseySelector.vue";
import TeamVersusIcon from "../../../TeamVersusIcon.vue";
import WinOption from "../../../WinOptionBranded.vue";
import LinkWrapper from "../../../LinkWrapper.vue";

import {
  injectTeamColors,
  injectUseTeamBadges
} from "../../../../lib/teamBranding";
import { injectUseTeamJerseys } from "../../../../lib/teamJerseys";
import {
  injectTemplateSize,
  LEADERBOARD,
  LARGE_LEADERBOARD
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { injectCardNumber, useAnalytics } from "../../../../hooks/useAnalytics";
import { SportCompetitor, SportEvent } from "@fresh8/types/src/betData";
import { TemplateSize } from "@fresh8/types/src";

const useStyles = createUseStyles({
  badgeLink: {
    zIndex: 999
  },
  badge: {
    maxWidth: props =>
      props.isCarousel ? "59px !important" : "63px !important",
    height: props => (props.isCarousel ? "59px" : "63px")
  },
  jersey: {
    width: props => (props.isCarousel ? "59px !important" : "63px !important")
  },
  badgeLeft: {
    margin: "6px 1px 6px 4px !important"
  },
  badgeRight: {
    margin: "6px 4px 6px 1px !important"
  },
  body: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  content: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexGrow: 1
  },
  header: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    justifyItems: "end",
    alignItems: "center",
    padding: "0 4px"
  },
  marketName: {
    "&.market-name": {
      fontSize: "9px"
    }
  },
  odds: {
    display: "flex",
    flexDirection: "column",
    "&.center": {
      width: props => (props.isCarousel ? "38px" : "46px"),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingBottom: "10px"
    },
    "&.left": {
      flexGrow: 1
    },
    "&.right": {
      flexGrow: 1
    }
  },
  option: {
    [`&.${LEADERBOARD.name}`]: {
      "& .banner-left": {
        paddingRight: "0px",
        paddingLeft: props => (props.noBadges ? "6px" : ""),
        "& .banner-svg": {
          width: props => props.regular.left.width,
          left: props => props.regular.left.displacement
        }
      },
      "& .banner-right": {
        paddingLeft: "0px",
        paddingRight: props => (props.noBadges ? "6px" : ""),
        "& .banner-svg": {
          width: props => props.regular.right.width,
          left: props => props.regular.right.displacement
        }
      }
    },
    [`&.${LARGE_LEADERBOARD.name}`]: {
      "& .banner-left": {
        paddingRight: "0px",
        paddingLeft: props => (props.noBadges ? "6px" : ""),
        "& .banner-svg": {
          width: props => props.large.left.width,
          left: props => props.large.left.displacement
        }
      },
      "& .banner-right": {
        paddingLeft: "0px",
        paddingRight: props => (props.noBadges ? "6px" : ""),
        "& .banner-svg": {
          width: props => props.large.right.width,
          left: props => props.large.right.displacement
        }
      }
    }
  },
  wrapper: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column"
  }
});

export default defineComponent({
  name: "Card",
  components: {
    LinkWrapper,
    MarketCard,
    TeamBadge,
    TeamJersey,
    TeamVersusIcon,
    CardHeaderLeaderboard,
    MarketName,
    WinOption
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber },
      "Event Name"
    );

    const { teamBranding, brand, adTemplateBranding } =
      injectBrand() || ({} as BrandContext);

    const marketId = ref([props.marketId]);
    const ftrMarkets = composeHomeAwayOptions(marketId);
    const badgesEnabled = injectUseTeamBadges();
    const jerseysEnabled = injectUseTeamJerseys();
    const template = injectTemplateSize() as {
      size: ComputedRef<TemplateSize>;
    };

    const teamColorsContext = injectTeamColors();

    const showBadges = computed(
      () => teamColorsContext.teamIconsAvailable.value
    );

    const isCarousel = computed(() => injectCarouselContext().value.isCarousel);

    const options = computed(() => [
      composeBetOption(ftrMarkets.value[0].options[0]).value,
      composeBetOption(ftrMarkets.value[0].options[1]).value
    ]);

    const competitors = composeEventCompetitors(props.eventId) as ComputedRef<
      SportCompetitor[]
    >;

    const stroke = computed(() => teamBranding.value.badge.borderColor);

    const styleVars = computed(() => ({
      ...adTemplateBranding.value,
      isCarousel: isCarousel.value,
      noBadges: !showBadges.value,
      regular: {
        left: {
          width: !showBadges.value
            ? isCarousel.value
              ? "191px"
              : "263px"
            : isCarousel.value
            ? "137px"
            : "204px",
          displacement: !showBadges.value ? "" : "-11px"
        },
        right: {
          width: !showBadges.value
            ? isCarousel.value
              ? "192px"
              : "263px"
            : isCarousel.value
            ? "137px"
            : "204px",
          displacement: ""
        }
      },
      large: {
        left: {
          width: !showBadges.value
            ? isCarousel.value
              ? "312px"
              : "385px"
            : isCarousel.value
            ? "257px"
            : "325px",
          displacement: !showBadges.value ? "" : "-11px"
        },
        right: {
          width: !showBadges.value
            ? isCarousel.value
              ? "312px"
              : "385px"
            : isCarousel.value
            ? "257px"
            : "325px",
          displacement: ""
        }
      }
    }));

    const classesRef = useStyles(styleVars);

    return {
      colors: teamColorsContext.colors,
      event,
      handleClick,
      badgesEnabled,
      classes: classesRef,
      competitors,
      ftrMarkets,
      jerseysEnabled,
      options,
      showBadges,
      stroke,
      useTeamColors: teamColorsContext.teamColorsAvailable,
      layoutClass: computed(() => template.size.value.name)
    };
  }
});
</script>

<style></style>
